import React from 'react'
import {
    WemoloArrowLeftIcon,
    WemoloArrowRightIcon,
    WemoloButton,
    WemoloChevronLeftIcon,
    WemoloChevronRightIcon,
    WemoloTypography
} from '../../../components/Wemolo'
import { DETECTION_EXPLORER_TEXT } from '../config'
import { buildPageItems } from './buildPageItems'
import { usePaginationSlotCount } from './usePaginationSlotCount'
import styles from './Pagination.module.css'

export type PaginationUIProps = {
    page: number
    totalPages: number
    filtering?: boolean
    hasMore?: boolean
    onChange: (page: number) => void
}

export const PaginationUI: React.FC<PaginationUIProps> = ({ page, totalPages, filtering, hasMore, onChange }) => {
    const slotCount = usePaginationSlotCount()
    const atStart = page <= 1
    const atEnd = page >= totalPages

    // Filtered mode: total match count is unknown, so show prev/current/next only — no first/last or
    // page list — with Next gated on `hasMore`.
    if (filtering) {
        return (
            <div className={styles.pagination}>
                <WemoloButton
                    view="secondary"
                    size="small"
                    icon={<WemoloChevronLeftIcon />}
                    disabled={atStart}
                    onClick={() => onChange(page - 1)}
                    aria-label={DETECTION_EXPLORER_TEXT.pagination.previous}
                />
                <span className={styles.pageActive}>
                    <WemoloButton view="ghost" size="small" aria-current="page" aria-label={`Page ${page}`}>
                        {page}
                    </WemoloButton>
                </span>
                <WemoloButton
                    view="secondary"
                    size="small"
                    icon={<WemoloChevronRightIcon />}
                    disabled={!hasMore}
                    onClick={() => onChange(page + 1)}
                    aria-label={DETECTION_EXPLORER_TEXT.pagination.next}
                />
            </div>
        )
    }

    const items = buildPageItems(page, totalPages, slotCount)

    return (
        <div className={styles.pagination}>
            <WemoloButton
                view="secondary"
                size="small"
                icon={<WemoloArrowLeftIcon />}
                disabled={atStart}
                onClick={() => onChange(1)}
                aria-label={DETECTION_EXPLORER_TEXT.pagination.first}
            />
            <WemoloButton
                view="secondary"
                size="small"
                icon={<WemoloChevronLeftIcon />}
                disabled={atStart}
                onClick={() => onChange(page - 1)}
                aria-label={DETECTION_EXPLORER_TEXT.pagination.previous}
            />

            {items.map((item) => {
                if (item === 'ellipsis-left' || item === 'ellipsis-right') {
                    return (
                        <WemoloTypography key={item} variant="body-medium" tag="span" className={styles.ellipsis}>
                            {DETECTION_EXPLORER_TEXT.pagination.ellipsis}
                        </WemoloTypography>
                    )
                }
                const isActive = item === page
                return (
                    <span key={item} className={isActive ? styles.pageActive : styles.pageInactive}>
                        <WemoloButton
                            view="ghost"
                            size="small"
                            onClick={() => onChange(item)}
                            aria-label={`Page ${item}`}
                            aria-current={isActive ? 'page' : undefined}
                        >
                            {item}
                        </WemoloButton>
                    </span>
                )
            })}

            <WemoloButton
                view="secondary"
                size="small"
                icon={<WemoloChevronRightIcon />}
                disabled={atEnd}
                onClick={() => onChange(page + 1)}
                aria-label={DETECTION_EXPLORER_TEXT.pagination.next}
            />
            <WemoloButton
                view="secondary"
                size="small"
                icon={<WemoloArrowRightIcon />}
                disabled={atEnd}
                onClick={() => onChange(totalPages)}
                aria-label={DETECTION_EXPLORER_TEXT.pagination.last}
            />
        </div>
    )
}
